import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import KeySignatureDisplayControl from 'NotationDisplay/KeySignatureDisplayControl';
import ScaleFingerNumbersControl from 'NotationDisplay/ScaleFingerNumbersControl';
import { MainAppReducer } from 'Types';
import * as appActions from '../../Actions/app';
import { CustomSwitch } from 'Components';
import { useMidiContext } from 'Contexts/MidiContext';
import {
  isBluetoothMidiPairingSupported,
  presentBluetoothMidiPairing,
} from 'Utils/MuseFlowMidiPlugin';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import {
  SCORE_THEME_LABELS,
  SCORE_THEMES,
  applyScoreThemeToSvgElement,
  getScoreThemePalette,
  normalizeScoreTheme,
  scoreThemeToPracticeDarkMode,
} from 'Utils/ScoreTheme';
import type { ScoreTheme } from 'Utils/ScoreTheme';
import AndroidBluetoothKeyboard from './AndroidBluetoothKeyboard';
import { OpenSheetMusicDisplay as OSMD } from '@museflow-ai/opensheetmusicdisplay';

const SCORE_THEME_PREVIEW_WIDTH = 500;
const SCORE_THEME_PREVIEW_HEIGHT = 146;
const SCORE_THEME_PREVIEW_TOP_INSET = -86;
const SCORE_THEME_PREVIEW_RENDER_SCALE = 1.5;

const SCORE_THEME_PREVIEW_MUSIC_XML = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Piano</part-name>
    </score-part>
  </part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>1</divisions>
        <key>
          <fifths>0</fifths>
        </key>
        <time>
          <beats>4</beats>
          <beat-type>4</beat-type>
        </time>
        <clef>
          <sign>G</sign>
          <line>2</line>
        </clef>
      </attributes>
      <note>
        <pitch><step>C</step><octave>5</octave></pitch>
        <duration>1</duration>
        <type>quarter</type>
      </note>
      <note>
        <pitch><step>E</step><octave>5</octave></pitch>
        <duration>1</duration>
        <type>quarter</type>
      </note>
      <note>
        <pitch><step>G</step><octave>5</octave></pitch>
        <duration>1</duration>
        <type>quarter</type>
      </note>
      <note>
        <rest/>
        <duration>1</duration>
        <type>quarter</type>
      </note>
    </measure>
  </part>
</score-partwise>`;

const ScoreThemePreview = ({ scoreTheme }: { scoreTheme: ScoreTheme }) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const scorePalette = getScoreThemePalette(scoreTheme);
  const isDarkScore = scoreThemeToPracticeDarkMode(scoreTheme);
  const borderColor =
    scoreTheme === 'dimPaper'
      ? 'rgba(56, 45, 37, 0.22)'
      : isDarkScore
        ? 'rgba(243, 247, 255, 0.16)'
        : 'rgba(15, 23, 42, 0.14)';

  useEffect(() => {
    const previewElement = previewRef.current;
    if (!previewElement) return;

    let cancelled = false;
    previewElement.innerHTML = '';
    previewElement.style.width = `${SCORE_THEME_PREVIEW_WIDTH}px`;
    previewElement.style.maxWidth = '100%';
    previewElement.style.height = `${SCORE_THEME_PREVIEW_HEIGHT - SCORE_THEME_PREVIEW_TOP_INSET}px`;
    previewElement.style.overflow = 'hidden';

    const osmd = new OSMD(previewElement, {
      drawTitle: false,
      drawTimeSignatures: true,
      drawMeasureNumbers: false,
      drawFingerings: true,
      drawCredits: false,
      drawPartNames: false,
      autoResize: false,
      stretchLastSystemLine: false,
      disableCursor: true,
      drawingParameters: 'compacttight',
    });
    const palette = getScoreThemePalette(scoreTheme);
    osmd.EngravingRules.DefaultVexFlowNoteFont = 'gonville';
    osmd.EngravingRules.PageTopMargin = 4;
    osmd.EngravingRules.PageBottomMargin = 1;
    osmd.EngravingRules.PageLeftMargin = 1;
    osmd.EngravingRules.PageRightMargin = 1;
    osmd.EngravingRules.SystemLeftMargin = 0;
    osmd.EngravingRules.SystemRightMargin = 0;
    osmd.EngravingRules.StaffLineColor = palette.staffLineColor;
    osmd.EngravingRules.PageBackgroundColor = palette.scoreBackground;
    osmd.EngravingRules.DefaultColorMusic = palette.notationColor;
    osmd.EngravingRules.DefaultColorNotehead = palette.notationColor;
    osmd.EngravingRules.DefaultColorRest = palette.notationColor;
    osmd.EngravingRules.DefaultColorStem = palette.notationColor;
    osmd.EngravingRules.DefaultColorLabel = palette.musicMarkingColor;
    osmd.EngravingRules.DefaultColorLyrics = palette.musicMarkingColor;
    osmd.EngravingRules.DefaultColorChordSymbol = palette.musicMarkingColor;
    osmd.EngravingRules.DefaultColorTitle = palette.musicMarkingColor;

    const renderPreview = async () => {
      try {
        await osmd.load(SCORE_THEME_PREVIEW_MUSIC_XML);
        if (cancelled) return;
        osmd.render();
        previewElement
          .querySelectorAll<HTMLElement>('svg, .osmd-svg-page, .osmd-page')
          .forEach((element) => {
            element.style.width = `${SCORE_THEME_PREVIEW_WIDTH}px`;
            element.style.maxWidth = `${SCORE_THEME_PREVIEW_WIDTH}px`;
            element.style.overflow = 'hidden';
          });
        previewElement.querySelectorAll<HTMLElement>('svg').forEach((svg) => {
          svg.style.transform = `scale(${SCORE_THEME_PREVIEW_RENDER_SCALE})`;
          svg.style.transformOrigin = 'left top';
        });
        applyScoreThemeToSvgElement(previewElement, scoreTheme);
      } catch {
        previewElement.innerHTML = '';
      }
    };

    void renderPreview();

    return () => {
      cancelled = true;
      previewElement.innerHTML = '';
    };
  }, [scoreTheme]);

  return (
    <Box
      sx={{
        marginTop: '14px',
        width: `${SCORE_THEME_PREVIEW_WIDTH}px`,
        maxWidth: `${SCORE_THEME_PREVIEW_WIDTH}px`,
        height: `${SCORE_THEME_PREVIEW_HEIGHT}px`,
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        clipPath: 'inset(0)',
        contain: 'paint',
        backgroundColor: scorePalette.scoreBackground,
        border: `1px solid ${borderColor}`,
        boxShadow: isDarkScore
          ? '0 16px 32px rgba(0, 0, 0, 0.24)'
          : '0 12px 28px rgba(0, 0, 0, 0.18)',
        '& svg': {
          display: 'block',
          width: `${SCORE_THEME_PREVIEW_WIDTH}px !important`,
          maxWidth: `${SCORE_THEME_PREVIEW_WIDTH}px !important`,
          height: `${SCORE_THEME_PREVIEW_HEIGHT - SCORE_THEME_PREVIEW_TOP_INSET}px !important`,
          backgroundColor: scorePalette.scoreBackground,
        },
        '& .osmd-svg-page': {
          backgroundColor: scorePalette.scoreBackground,
        },
      }}
    >
      <Box
        ref={previewRef}
        aria-label={`${SCORE_THEME_LABELS[scoreTheme]} score preview`}
        role="img"
        sx={{
          position: 'absolute',
          top: `${SCORE_THEME_PREVIEW_TOP_INSET}px`,
          left: 0,
          width: `${SCORE_THEME_PREVIEW_WIDTH}px`,
          height: `${SCORE_THEME_PREVIEW_HEIGHT - SCORE_THEME_PREVIEW_TOP_INSET}px`,
          overflow: 'hidden',
        }}
      />
    </Box>
  );
};

const Keyboard = () => {
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);

  const { userData } = data;

  const dispatch = useDispatch();
  const { preferredInputMode, setPreferredInputMode } = useMidiContext();
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const showPreferredInputSelection = audioRecognitionEnabled;
  // The key-signature setting only changes how Exercises write scales.
  const showExercises = useFeatureFlag(FeatureFlag.USE_EXERCISES);
  const scoreTheme = normalizeScoreTheme(
    userData?.practice_score_theme,
    userData?.practice_dark_mode,
  );
  const setScoreTheme = (nextScoreTheme: ScoreTheme) => {
    dispatch(
      appActions.updateUserData({
        practice_score_theme: nextScoreTheme,
        practice_dark_mode: scoreThemeToPracticeDarkMode(nextScoreTheme),
      }),
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', overflow: 'scroll' }}>
      <Box
        sx={{
          marginLeft: '50px',
          width: '600px',
          backgroundColor: 'black',
          borderRadius: '5px',
        }}
      >
        <Box
          sx={{
            color: 'white',
            textAlign: 'left',
            marginLeft: '24px',
            marginRight: '24px',
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              marginTop: '20px',
              marginBottom: '20px',
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: '26px',
              letterSpacing: '0.01em',
              color: '#FFFFFF',
              fontFamily: 'Lato',
            }}
          >
            Practice Settings
          </Box>
          {/* <Box sx={{marginBottom: '20px', display: 'flex', flexDirection: 'column'}}>
          <Box sx={{display:'flex', alignItems:'center', height: '25px'}}><img style={{height:"100%", marginRight: '10px'}} src={pianoIcon}/> Keyboard Range:</Box>
          <Box sx={{marginTop: '10px', marginLeft: '35px', display: 'flex'}}>
            <Box sx={{
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: '16px',
              letterSpacing: '0.01em',
              color: '#ECECEC',
            }}>88 keys</Box>
            <Box sx={{
              marginLeft: '7px',
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: '16px',
              letterSpacing: '0.01em',
              color: '#767C8C',
            }}>A0-C8</Box>
            <Box sx={{
              marginLeft: '7px',
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: '16px',
              letterSpacing: '0.01em',
              textDecorationLine: 'underline',
              color: '#469F6A',
            }}>Edit</Box>
          </Box>
        </Box> */}
          <Box sx={{ marginBottom: '20px', fontFamily: 'Lato' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px',
                marginTop: '5px',
              }}
            >
              <Box
                sx={{
                  fontStyle: 'normal',
                  fontWeight: '400',
                  fontSize: '16px',
                  letterSpacing: '0.01em',
                  color: userData?.audio_on ? '#767C8C' : '#ECECEC',
                  transition: 'color 0.2s',
                }}
              >
                Keyboard Speakers
              </Box>
              <CustomSwitch
                checked={userData?.audio_on ?? true}
                onChange={(e) => {
                  dispatch(appActions.setAudioOn({ audioOn: e.target.checked }));
                }}
              />
              <Box
                sx={{
                  fontStyle: 'normal',
                  fontWeight: '400',
                  fontSize: '16px',
                  letterSpacing: '0.01em',
                  color: userData?.audio_on ? '#ECECEC' : '#767C8C',
                  transition: 'color 0.2s',
                }}
              >
                Device Speakers
              </Box>
            </Box>
          </Box>

          {isBluetoothMidiPairingSupported() && (
            <Box sx={{ marginBottom: '24px', fontFamily: 'Lato' }}>
              <Box
                sx={{
                  fontStyle: 'normal',
                  fontWeight: '400',
                  fontSize: '16px',
                  letterSpacing: '0.01em',
                  color: '#ECECEC',
                  marginBottom: '10px',
                }}
              >
                Bluetooth keyboard
              </Box>
              <Box
                component="button"
                onClick={() => {
                  void presentBluetoothMidiPairing();
                }}
                sx={{
                  px: '20px',
                  py: '10px',
                  borderRadius: '10px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: '#4A90D9',
                  color: '#FFFFFF',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  '&:hover': { opacity: 0.85 },
                }}
              >
                Connect Bluetooth keyboard
              </Box>
            </Box>
          )}

          {showPreferredInputSelection && (
            <Box sx={{ marginBottom: '24px', fontFamily: 'Lato' }}>
              <Box
                sx={{
                  fontStyle: 'normal',
                  fontWeight: '400',
                  fontSize: '16px',
                  letterSpacing: '0.01em',
                  color: '#ECECEC',
                  marginBottom: '10px',
                }}
              >
                Preferred Input
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  onClick={() => {
                    setPreferredInputMode('midi');
                  }}
                  sx={{
                    padding: '10px 14px',
                    borderRadius: '999px',
                    border:
                      preferredInputMode === 'midi'
                        ? '1px solid #7FD7FF'
                        : '1px solid rgba(255,255,255,0.18)',
                    backgroundColor:
                      preferredInputMode === 'midi' ? 'rgba(127, 215, 255, 0.14)' : 'transparent',
                    color: preferredInputMode === 'midi' ? '#ECECEC' : '#9CA3AF',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  MIDI Keyboard
                </Box>
                <Box
                  onClick={() => {
                    if (!audioRecognitionEnabled) return;
                    setPreferredInputMode('audio');
                  }}
                  sx={{
                    padding: '10px 14px',
                    borderRadius: '999px',
                    border:
                      preferredInputMode === 'audio'
                        ? '1px solid #7FD7FF'
                        : '1px solid rgba(255,255,255,0.18)',
                    backgroundColor:
                      preferredInputMode === 'audio' ? 'rgba(127, 215, 255, 0.14)' : 'transparent',
                    color: audioRecognitionEnabled
                      ? preferredInputMode === 'audio'
                        ? '#ECECEC'
                        : '#9CA3AF'
                      : '#5B6474',
                    cursor: audioRecognitionEnabled ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Audio Recognition
                </Box>
              </Box>
              <Box
                sx={{
                  marginTop: '10px',
                  fontSize: '13px',
                  color: 'rgba(236,236,236,0.62)',
                }}
              >
                Default is MIDI. This selector is only exposed in local/dev builds.
              </Box>
            </Box>
          )}

          <AndroidBluetoothKeyboard />

          <Box sx={{ marginBottom: '24px', fontFamily: 'Lato' }}>
            <Box
              sx={{
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '16px',
                letterSpacing: '0.01em',
                color: '#ECECEC',
                marginBottom: '10px',
              }}
            >
              Score Theme
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '10px',
                flexWrap: 'wrap',
              }}
            >
              {SCORE_THEMES.map((theme) => {
                const selected = scoreTheme === theme;
                return (
                  <Box
                    key={theme}
                    onClick={() => setScoreTheme(theme)}
                    sx={{
                      padding: '10px 14px',
                      borderRadius: '999px',
                      border: selected ? '1px solid #7FD7FF' : '1px solid rgba(255,255,255,0.18)',
                      backgroundColor: selected ? 'rgba(127, 215, 255, 0.14)' : 'transparent',
                      color: selected ? '#ECECEC' : '#9CA3AF',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {SCORE_THEME_LABELS[theme]}
                  </Box>
                );
              })}
            </Box>
            <ScoreThemePreview scoreTheme={scoreTheme} />
          </Box>

          {showExercises && <KeySignatureDisplayControl />}
          {showExercises && <ScaleFingerNumbersControl />}
        </Box>
      </Box>
    </Box>
  );
};

export default Keyboard;
